import {
    faComments,
    faExclamationTriangle,
    faPencil,
    faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React, { useEffect, useState } from "react";

import MessageForm, { MessageFormData } from "@/mk2/apps/bazaar/components/MessageForm";
import Messages from "@/mk2/apps/bazaar/components/Messages";
import ProductBuyerHeader from "@/mk2/apps/bazaar/components/ProductBuyerHeader";
import ReviewForm, { ReviewFormData } from "@/mk2/apps/bazaar/components/ReviewForm";
import { MESSAGE_REPLY_FORM, REVIEW_FORM } from "@/mk2/apps/bazaar/helpers";
import {
    BazaarMessageEntity,
    BuyerEntity,
    ProductQuality,
    ReviewEntity,
} from "@/mk2/apps/bazaar/schemas";
import { faqBuyerReportUrl } from "@/mk2/apps/faq/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Panel } from "@/mk2/components/Panel";
import useEventCallback from "@/mk2/hooks/useEventCallback";
import { UserEntity } from "@/mk2/schemas";
import { interpolate } from "@/mk2/services/i18n";
import {
    BZ2_REMOVE_BUYER,
    BZ2_REPLY,
    BZ2_REPLY_TO_seller,
    BZ2_REVIEW_BUYER,
    BZ2_REVIEW_SELLER,
} from "@/mk/autogenerated/translation";
import { isAuthenticatedAs } from "@/mk/bazaar/common/userUtils";

import styles from "./ProductBuyer.mscss";

const fasCommentsLg: FontAwesomeIconProps = { icon: faComments, size: "lg" };
const fasTrashLg: FontAwesomeIconProps = { icon: faTrash, size: "lg" };
const fasPencilLg: FontAwesomeIconProps = { icon: faPencil, size: "lg" };

const reviewInitialValues: ReviewFormData = {
    message: "",
    photos: [],
    rating: "",
};

const messageInitialValues: MessageFormData = {
    message: "",
    photos: [],
};

enum AddingState {
    NO,
    MESSAGE,
    REVIEW,
}

interface OwnProps {
    className?: string;
    buyer: BuyerEntity;
    messages: BazaarMessageEntity[];
    reviews: ReviewEntity[];
    requestUser: UserEntity;
    requestUserPermissions: string[];

    onRead(messagesIds: number[]);
    onNewReview(buyerId: number, values: ReviewFormData);
    onNewMessage(buyerId: number, values: MessageFormData);
    onRemoveBuyer(buyerId: number);
}

type Props = OwnProps;

const ProductBuyer: React.FunctionComponent<Props> = ({
    className,
    buyer,
    requestUser,
    requestUserPermissions,
    messages,
    reviews,
    onRead,
    onNewReview,
    onNewMessage,
    onRemoveBuyer,
}) => {
    const isBuyer = isAuthenticatedAs(requestUser, buyer.user);

    // @see Buyer.can_review() on server
    // There has to be message from seller and no review in last 10 days
    const cooldown = Date.now() - 10 * 24 * 60 * 60 * 1000;
    const canReview =
        buyer.messagesCount > 0 &&
        messages.some((m) => m.isSeller) &&
        reviews.every(
            (r) =>
                new Date(r.date).getTime() < cooldown ||
                !isAuthenticatedAs(requestUser, r.reviewer),
        );

    const [addingState, setAddingState] = useState<AddingState>(AddingState.NO);

    const onReviewFormOpenClick = useEventCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            setAddingState(AddingState.REVIEW);
        },
        [setAddingState],
    );

    const onMessageFormOpenClick = useEventCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            setAddingState(AddingState.MESSAGE);
        },
        [setAddingState],
    );

    const onCloseClick = useEventCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            setAddingState(AddingState.NO);
        },
        [setAddingState],
    );

    const onReviewSave = useEventCallback(
        (values: ReviewFormData) => {
            onNewReview(buyer.id, values);
        },
        [buyer, onNewReview],
    );

    const onMessageSave = useEventCallback(
        (values: ReviewFormData) => {
            onNewMessage(buyer.id, values);
        },
        [buyer, onNewMessage],
    );

    const onRemoveBuyerCallback = useEventCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            onRemoveBuyer(buyer.id);
        },
        [buyer, onRemoveBuyer],
    );

    useEffect(() => {
        // Reset adding state to NO when we add new review or message
        setAddingState(AddingState.NO);
    }, [messages.length + reviews.length, setAddingState]);

    return (
        <Panel className={cx(styles.ProductBuyer, className)}>
            {!isBuyer && <ProductBuyerHeader buyer={buyer} />}

            <Messages
                buyer={buyer}
                messages={messages}
                reviews={reviews}
                requestUser={requestUser}
                requestUserPermissions={requestUserPermissions}
                onRead={onRead}
            />

            <div className={styles.ProductBuyer__footer}>
                {addingState === AddingState.REVIEW ? (
                    <ReviewForm
                        form={REVIEW_FORM(buyer.id)}
                        initialValues={reviewInitialValues}
                        onSave={onReviewSave}
                        onCancel={onCloseClick}
                        asBuyer={isBuyer}
                    />
                ) : addingState === AddingState.MESSAGE ? (
                    <MessageForm
                        form={MESSAGE_REPLY_FORM(buyer.id)}
                        initialValues={messageInitialValues}
                        onSave={onMessageSave}
                        onCancel={onCloseClick}
                    />
                ) : (
                    <div className={styles.ProductBuyer__actions}>
                        <div className={styles.ProductBuyer__actions__left}>
                            {!buyer.product.state.isDeleted && (
                                <Btn
                                    className={styles.ProductBuyer__messageAction}
                                    type={BtnType.Blue}
                                    icon={fasCommentsLg}
                                    label={
                                        isBuyer &&
                                        ![ProductQuality.SERVICES, ProductQuality.JOBS].includes(
                                            buyer.product.quality,
                                        )
                                            ? interpolate(BZ2_REPLY_TO_seller, {
                                                  seller: buyer.product.seller.username,
                                              })
                                            : BZ2_REPLY
                                    }
                                    onClick={onMessageFormOpenClick}
                                />
                            )}
                            {!isBuyer && (
                                <Btn
                                    className={styles.ProductBuyer__removeAction}
                                    type={BtnType.BlueOutline}
                                    icon={fasTrashLg}
                                    label={BZ2_REMOVE_BUYER}
                                    onClick={onRemoveBuyerCallback}
                                />
                            )}
                            {!isBuyer && (
                                <Btn
                                    className={styles.ProductBuyer__reportAction}
                                    type={BtnType.BlueOutline}
                                    icon={faExclamationTriangle}
                                    link={faqBuyerReportUrl}
                                />
                            )}
                        </div>
                        <div className={styles.ProductBuyer__actions__right}>
                            {canReview && (
                                <Btn
                                    className={styles.ProductBuyer__reviewAction}
                                    type={BtnType.BlueOutline}
                                    icon={fasPencilLg}
                                    label={isBuyer ? BZ2_REVIEW_SELLER : BZ2_REVIEW_BUYER}
                                    onClick={onReviewFormOpenClick}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Panel>
    );
};

ProductBuyer.displayName = "ProductBuyer";

export default React.memo<Props>(ProductBuyer);
